import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import Radio from '@carvana/showroom-forms/Radio';
import { Field, Formik } from 'formik';
import { Spinner } from '@carvana/showroom-spinner';
import { connectBankFormValidationSchema } from './helpers/validationHelper';
import type { RootState } from '../../../store/configureStore';
import { MyCarsService } from '../../../services/index';
import BankSuccess from './BankSuccess/BankSuccess';
import BankError from './BankError/BankError';
import {
  ConnectBankButton,
  ConnectBankCardWrapper,
  ConnectBankFieldset,
  ConnectBankInput,
  ConnectBankTitle,
} from './ConnectBankCard.styles';
import { PaymentMethodType } from '../../../constants';
import { useDualEventTracking } from '../../../utils/useDualEventTracking';

const ConnectBankCard = ({ vehicleId }: any) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isBankSuccess, setBankSuccess] = useState(false);
  const [isBankError, setBankError] = useState(false);
  const [isInvalidRoutingError, setInvalidRoutingError] = useState(false);
  const { trackEvent } = useDualEventTracking();

  const myCars = useSelector((state: RootState) => state.myCars.cars);
  const loanDetailCar: any = myCars.find(
    (car: any) => car.vehicle.vehicleId === Number(vehicleId)
  );

  const last4OfPaymentMethod =
    loanDetailCar.bankInfo[0] && loanDetailCar.bankInfo[0].last4OfPaymentMethod
      ? loanDetailCar.bankInfo[0].last4OfPaymentMethod
      : '';
  const paymentMethodNickName =
    loanDetailCar.bankInfo[0] && loanDetailCar.bankInfo[0].paymentMethodNickName
      ? loanDetailCar.bankInfo[0].paymentMethodNickName
      : '';

  const bankAccountSelected = (acctType: string) => {
    if (acctType.toLowerCase() === PaymentMethodType.CHECKING.toLowerCase()) {
      trackEvent('bank_info_checking_button_tap');
    }
    if (acctType.toLowerCase() === PaymentMethodType.SAVING.toLowerCase()) {
      trackEvent('bank_info_savings_button_tap');
    }
  };

  const handleBankSubmit = (resp: any) => {
    setIsSubmitting(false);
    // determine if dealing with error response or
    // success response
    const success = resp.data?.isSuccess;
    if (success === true) {
      setBankSuccess(true);
    } else if (
      resp.response.data.message.includes('Invalid Routing Number entered')
    ) {
      setInvalidRoutingError(true);
    } else {
      setBankError(true);
    }
  };

  return (
    <>
      {isSubmitting && (
        <Spinner
          className="spinnerWrapper fullScreenSpinner"
          isFullScreen
          opaqueBackground
        />
      )}
      {!loanDetailCar.bankInfo || loanDetailCar.bankInfo.length <= 0 ? (
        <ConnectBankTitle>Let&apos;s connect you to your bank</ConnectBankTitle>
      ) : (
        <ConnectBankTitle>Update Bank Details</ConnectBankTitle>
      )}

      <ConnectBankCardWrapper>
        {isBankSuccess && <BankSuccess isOpen={isBankSuccess} />}
        {isBankError && (
          <BankError
            isOpen={isBankError}
            setBankError={setBankError}
            category="General"
          />
        )}
        {isInvalidRoutingError && (
          <BankError
            isOpen={isInvalidRoutingError}
            setBankError={setInvalidRoutingError}
            category="Invalid Routing"
          />
        )}
        <Formik
          initialValues={{
            bankNickname: `${paymentMethodNickName}`,
            acctType: PaymentMethodType.CHECKING.toUpperCase(),
            routingNumber: '',
            accountNumber: '',
            confirmAccountNumber: '',
          }}
          validationSchema={connectBankFormValidationSchema}
          onSubmit={(values) => {
            setIsSubmitting(true);
            trackEvent('bank_info_submit_continue_button_tap');
            const connectBankRequest = {
              isSaveForReuse: true,
              last4OfPaymentMethod,
              paymentMethodNickName: values.bankNickname,
              routingNumber: values.routingNumber,
              bankAccountNumber: values.accountNumber,
              paymentMethodType: values.acctType,
            };

            MyCarsService.saveBankInfo(
              process.env.NODE_ENV,
              vehicleId,
              connectBankRequest
            )
              .then((res) => {
                setIsSubmitting(false);
                handleBankSubmit(res);
              })
              .catch((err) => {
                setIsSubmitting(false);
                console.error(err);
              });
          }}
          render={({
            touched,
            handleSubmit,
            handleChange,
            handleBlur,
            values,
            errors,
          }) => {
            const isSubmitButtonDisabled =
              errors.bankNickname ||
              errors.routingNumber ||
              errors.accountNumber ||
              errors.confirmAccountNumber ||
              isSubmitting;

            return (
              <form onSubmit={handleSubmit} noValidate>
                <ConnectBankInput
                  id="bank-nickname"
                  mode="light"
                  label="Bank Nickname"
                  name="bankNickname"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.bankNickname}
                  touched={touched.bankNickname}
                  error={errors.bankNickname}
                />
                <ConnectBankFieldset>
                  <Field
                    component={Radio}
                    name="acctType"
                    id="Checking"
                    label="Checking"
                    value="CHECKING"
                    onClick={() =>
                      bankAccountSelected(PaymentMethodType.CHECKING)
                    }
                  />
                  <Field
                    component={Radio}
                    name="acctType"
                    id="Savings"
                    label="Savings"
                    value="SAVINGS"
                    onClick={() =>
                      bankAccountSelected(PaymentMethodType.SAVING)
                    }
                  />
                </ConnectBankFieldset>
                <ConnectBankInput
                  id="routing-number"
                  pattern="[0-9]*"
                  inputMode="numeric"
                  mode="light"
                  label="Routing Number"
                  name="routingNumber"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.routingNumber}
                  touched={touched.routingNumber}
                  error={errors.routingNumber}
                />
                <ConnectBankInput
                  id="account-number"
                  pattern="[0-9]*"
                  inputMode="numeric"
                  mode="light"
                  label="Account Number"
                  name="accountNumber"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.accountNumber}
                  touched={touched.accountNumber}
                  error={errors.accountNumber}
                />
                <ConnectBankInput
                  id="confirm-account-number"
                  pattern="[0-9]*"
                  inputMode="numeric"
                  mode="light"
                  label="Confirm Account Number"
                  name="confirmAccountNumber"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.confirmAccountNumber}
                  touched={touched.confirmAccountNumber}
                  error={errors.confirmAccountNumber}
                />
                <ConnectBankButton
                  disabled={isSubmitButtonDisabled}
                  type="submit"
                >
                  {!loanDetailCar.bankInfo || loanDetailCar.bankInfo.length <= 0
                    ? 'Submit and Continue'
                    : 'Update and Save'}
                </ConnectBankButton>
              </form>
            );
          }}
        />
      </ConnectBankCardWrapper>
    </>
  );
};

export default ConnectBankCard;
